/**
 * GUPM-302 — identidade do deploy. TS puro: sem React, sem DOM, sem fetch.
 *
 * ## Por que o `<script type="module">` é a identidade
 *
 * O Vite versiona o chunk de entrada por CONTEÚDO (`/assets/index-<hash>.js`), e o
 * `index.html` aponta para ele. Então comparar o script do documento em execução com o
 * de um `index.html` buscado agora responde exatamente à pergunta que importa:
 * *o chunk que estou rodando ainda é o que o servidor entrega?*
 *
 * ## Por que NÃO o semver do `package.json`
 *
 * Ele só é bumpado manualmente no release. Dois deploys da mesma versão ficariam
 * indistinguíveis e um hotfix não dispararia aviso.
 *
 * ## Por que NÃO uma constante injetada no build
 *
 * Seria mais explícito (`define` no `vite.config.ts` com `CF_PAGES_COMMIT_SHA`, ou um
 * `version.json` gerado por script no `package.json`), mas os dois arquivos são
 * PROTEGIDOS pelas convenções do repo e exigem confirmação. Esta abordagem não precisa
 * de configuração de build nenhuma.
 *
 * Em desenvolvimento não é preciso `if (DEV)`: os dois lados leem o PRIMEIRO script de
 * módulo do MESMO servidor de dev, então batem por construção → nenhum aviso. (Qual
 * script é o primeiro depende da ordem em que o Vite injeta o dele, e a comparação não
 * depende disso.)
 */

/**
 * `src` do primeiro `<script type="module">` do HTML, ou `null`.
 *
 * Regex e não DOMParser de propósito: o domínio é puro (não pode tocar DOM/`window`) e
 * o alvo é o nosso próprio `index.html`, de forma estável e conhecida. Aceita atributos
 * em qualquer ordem (`type` antes ou depois de `src`) porque o Vite injeta
 * `crossorigin` entre eles no build.
 */
export function extrairScriptDeModulo(html: string): string | null {
  const tags = html.match(/<script\b[^>]*>/gi);
  if (!tags) return null;
  for (const tag of tags) {
    if (!/\btype\s*=\s*["']module["']/i.test(tag)) continue;
    const src = tag.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    if (src?.[1]) return src[1];
  }
  return null;
}

/**
 * Há versão nova? Só quando as DUAS identidades foram lidas e diferem.
 *
 * Identidade ausente ou vazia devolve `false`: falha de leitura não pode virar aviso
 * falso. Num app de segurança de barragem, aviso errado gasta a confiança que o aviso
 * certo vai precisar.
 */
export function temVersaoNova(local: string | null, remoto: string | null): boolean {
  if (!local || !remoto) return false;
  return local !== remoto;
}

/**
 * Assinaturas de `import()` que falhou por o chunk não existir mais no servidor.
 *
 * É um CONJUNTO porque o texto varia por motor, e o boundary não pode depender de um
 * navegador só. A última entrada é a causa real por trás do fallback de SPA: o asset
 * inexistente responde `200` com `content-type: text/html` (`/* /index.html 200` no
 * `public/_redirects`), e a checagem estrita de MIME type para módulos recusa executar.
 */
const ASSINATURAS_DE_CHUNK = [
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // WebKit
  /failed to load module script/i, // Chromium, camada de MIME type
  /expected a javascript(-or-wasm)? module script/i,
];

/**
 * O erro capturado é falha de carregamento de chunk, ou é um bug qualquer de render?
 *
 * Existe porque `componentDidCatch` pega tudo. Anunciar "nova versão" para um bug de
 * formulário é diagnóstico falso: a pessoa recarrega, reabre, estoura igual, mesma
 * mensagem — e o aviso de versão perde a credibilidade que ele existe para ter.
 *
 * Na dúvida devolve `false`: valor lançado que não é `Error` (o React entrega o que
 * lançaram, e nem tudo é `Error`) não deixa afirmar deploy novo. O boundary segue
 * segurando a tela branca de qualquer forma — o que muda é só o que ele DIZ.
 */
export function eFalhaDeChunk(erro: unknown): boolean {
  if (!(erro instanceof Error)) return false;
  // `webpack`/`Next` batizam a classe; o Vite não, mas custa nada reconhecer.
  if (erro.name === 'ChunkLoadError') return true;
  return ASSINATURAS_DE_CHUNK.some((assinatura) => assinatura.test(erro.message));
}
